/**
 * The question bank on the client (ADR 0015): labels, the catalog, the
 * filter as a query string, and the filter fields that the start dialog,
 * the Fragenbibliothek page and the editors' "Aus der Bibliothek" share.
 */
import {
  BANK_LEVELS,
  BANK_LIMITS,
  GEN_STEPS,
  type BankCatalog,
  type BankFilter,
} from '@schulspiele/protocol';

import { api } from './api';
import { esc } from './dom';
import './bank.css';

/** Where the editor page finds questions the Fragenbibliothek page sent (sessionStorage). */
export const BANK_TAKE_KEY = 'schulspiele:bank-take';

export const LEVEL_LABELS: Record<string, string> = {
  '5': 'Klasse 5',
  '6': 'Klasse 6',
  '7': 'Klasse 7',
  '8': 'Klasse 8',
  '9': 'Klasse 9',
  '10': 'Klasse 10',
  erw: 'Erwachsene',
  none: 'Ohne Stufe',
};

export function levelLabel(level: string): string {
  return LEVEL_LABELS[level] ?? level;
}

/** "Klasse 5, 6" or "Erwachsene". */
export function levelsText(levels: readonly string[]): string {
  if (levels.length === 0) return 'Ohne Stufe';
  const classes = levels.filter((l) => l !== 'erw');
  const parts = classes.length === 0 ? [] : [`Klasse ${classes.join(', ')}`];
  if (levels.includes('erw')) parts.push('Erwachsene');
  return parts.join(', ');
}

let catalogCache: Promise<BankCatalog> | null = null;

/** The catalog, loaded once per page; `fresh` after a change. */
export function loadCatalog(fresh = false): Promise<BankCatalog> {
  if (fresh || catalogCache === null) {
    catalogCache = api<BankCatalog>('/api/bank/catalog').catch((err: unknown) => {
      catalogCache = null;
      throw err;
    });
  }
  return catalogCache;
}

export function subjectName(cat: BankCatalog, id: string | null): string {
  if (id === null || id === 'none') return 'Ohne Fach';
  return cat.subjects.find((s) => s.id === id)?.name ?? id;
}

export function topicName(cat: BankCatalog, id: string | null): string {
  if (id === null || id === 'none') return 'Ohne Unterthema';
  for (const s of cat.subjects) {
    const t = s.topics.find((x) => x.id === id);
    if (t !== undefined) return t.name;
  }
  return id;
}

/** The filter as query parameters for `/api/bank/items`, `/tree`, `/count`, `/export`. */
export function filterQuery(f: BankFilter, extra: Record<string, string> = {}): string {
  const p = new URLSearchParams();
  if (f.levels !== undefined && f.levels.length > 0) p.set('levels', f.levels.join(','));
  if (f.subject !== undefined) p.set('subject', f.subject);
  if (f.topic !== undefined) p.set('topic', f.topic);
  if (f.difficultyMin !== undefined) p.set('dmin', String(f.difficultyMin));
  if (f.difficultyMax !== undefined) p.set('dmax', String(f.difficultyMax));
  if (f.types !== undefined && f.types.length > 0) p.set('types', f.types.join(','));
  if (f.q !== undefined && f.q.trim() !== '') p.set('q', f.q.trim());
  if (f.scope !== undefined) p.set('scope', f.scope);
  if (f.source !== undefined) p.set('source', f.source);
  if (f.playable !== undefined) p.set('playable', f.playable ? '1' : '0');
  if (f.drafts === true) p.set('drafts', '1');
  if (f.media === 'image') p.set('media', 'image');
  if (f.sensitive === true) p.set('sensitive', '1');
  if (f.liveOnly === true) p.set('live', '1');
  for (const [k, v] of Object.entries(extra)) p.set(k, v);
  const s = p.toString();
  return s === '' ? '' : `?${s}`;
}

/**
 * Difficulty as words for lists, in the Quiz-Generator's five steps
 * (1–2 sehr leicht, 3–4 leicht, 5–6 mittel, 7–8 schwer, 9–10 sehr schwer).
 */
export function difficultyText(d: number | null): string {
  if (d === null) return '';
  const step = GEN_STEPS[Math.max(0, Math.min(4, Math.floor((d - 1) / 2)))] ?? '';
  return `${step.toLowerCase()} (${String(d)})`;
}

/* ---- Filter fields ------------------------------------------------------------------------- */

export interface FilterFieldsOptions {
  /** A prefix for the field names, so two sets of fields can share a form. */
  prefix?: string;
  /** Show the "heikel" checkbox. */
  sensitive?: boolean;
}

/**
 * Stufe, Fach, Unterthema, Schwierigkeit von bis (and optionally "heikle
 * einschließen") as form fields. `bindFilterFields` keeps the subtopic
 * list in step with the subject; `readFilterFields` reads them back.
 */
export function filterFieldsHtml(
  cat: BankCatalog,
  initial: BankFilter,
  o: FilterFieldsOptions = {},
): string {
  const n = (name: string): string => `${o.prefix ?? 'bank'}-${name}`;
  const level = initial.levels?.[0] ?? '';
  const diff = (name: string, value: number | undefined, label: string): string =>
    `<label class="field bank-filter__diffpart"><span class="visually-hidden">Schwierigkeit </span>${label}<select name="${n(name)}">
      <option value="">beliebig</option>
      ${Array.from({ length: BANK_LIMITS.maxDifficulty }, (_, i) => i + 1)
        .map(
          (d) =>
            `<option value="${String(d)}" ${d === value ? 'selected' : ''}>${String(d)}</option>`,
        )
        .join('')}
    </select></label>`;
  return `<div class="bank-filter">
    <label class="field">Stufe<select name="${n('level')}">
      <option value="">Alle Stufen</option>
      ${BANK_LEVELS.map((l) => `<option value="${l}" ${l === level ? 'selected' : ''}>${esc(levelLabel(l))}</option>`).join('')}
    </select></label>
    <label class="field">Fach<select name="${n('subject')}">
      <option value="">Alle Fächer</option>
      ${cat.subjects.map((s) => `<option value="${esc(s.id)}" ${s.id === initial.subject ? 'selected' : ''}>${esc(s.name)}</option>`).join('')}
    </select></label>
    <label class="field">Unterthema<select name="${n('topic')}" data-bank-topic>${topicOptions(cat, initial.subject ?? '', initial.topic ?? '', level)}</select></label>
    <fieldset class="bank-filter__diff">
      <legend>Schwierigkeit <span class="muted">(1 sehr leicht, 10 sehr schwer)</span></legend>
      ${diff('dmin', initial.difficultyMin, 'von')}
      ${diff('dmax', initial.difficultyMax, 'bis')}
    </fieldset>
    ${
      o.sensitive === true
        ? `<label class="field field--check"><input type="checkbox" name="${n('sensitive')}" ${initial.sensitive === true ? 'checked' : ''} /><span>Heikle Fragen einschließen</span></label>`
        : ''
    }
  </div>`;
}

/**
 * The subtopics of a subject; with a level only those for that level (and
 * those without levels). The selected one always stays in the list.
 */
function topicOptions(cat: BankCatalog, subjectId: string, selected: string, level = ''): string {
  const subject = cat.subjects.find((s) => s.id === subjectId);
  if (subject === undefined) return '<option value="">Erst ein Fach wählen</option>';
  return `<option value="">Alle Unterthemen</option>${subject.topics
    .filter(
      (t) =>
        level === '' ||
        t.levels.length === 0 ||
        (t.levels as readonly string[]).includes(level) ||
        t.id === selected,
    )
    .map(
      (t) =>
        `<option value="${esc(t.id)}" ${t.id === selected ? 'selected' : ''}>${esc(t.name)}</option>`,
    )
    .join('')}`;
}

/** Keep the subtopic list in step with subject and level; `onChange` after every change. */
export function bindFilterFields(
  root: HTMLElement,
  cat: BankCatalog,
  onChange: () => void,
  o: FilterFieldsOptions = {},
): void {
  const n = (name: string): string => `${o.prefix ?? 'bank'}-${name}`;
  const level = root.querySelector<HTMLSelectElement>(`select[name="${n('level')}"]`);
  const subject = root.querySelector<HTMLSelectElement>(`select[name="${n('subject')}"]`);
  const topic = root.querySelector<HTMLSelectElement>(`select[name="${n('topic')}"]`);
  subject?.addEventListener('change', () => {
    if (topic !== null) topic.innerHTML = topicOptions(cat, subject.value, '', level?.value ?? '');
  });
  // A new level keeps the subtopic when it fits that level, else "Alle Unterthemen".
  level?.addEventListener('change', () => {
    if (topic === null || subject === null) return;
    const t = cat.subjects
      .find((x) => x.id === subject.value)
      ?.topics.find((x) => x.id === topic.value);
    const keep =
      t !== undefined &&
      (level.value === '' ||
        t.levels.length === 0 ||
        (t.levels as readonly string[]).includes(level.value));
    topic.innerHTML = topicOptions(cat, subject.value, keep ? t.id : '', level.value);
  });
  root.addEventListener('change', (e) => {
    const name = (e.target as HTMLElement).getAttribute('name') ?? '';
    if (name.startsWith(`${o.prefix ?? 'bank'}-`)) onChange();
  });
}

export function readFilterFields(root: HTMLElement, o: FilterFieldsOptions = {}): BankFilter {
  const n = (name: string): string => `${o.prefix ?? 'bank'}-${name}`;
  const value = (name: string): string =>
    root.querySelector<HTMLSelectElement | HTMLInputElement>(`[name="${n(name)}"]`)?.value ?? '';
  const f: BankFilter = {};
  if (value('level') !== '') f.levels = [value('level')];
  if (value('subject') !== '') f.subject = value('subject');
  if (value('topic') !== '') f.topic = value('topic');
  const dmin = Number(value('dmin'));
  const dmax = Number(value('dmax'));
  if (value('dmin') !== '') f.difficultyMin = dmin;
  if (value('dmax') !== '') f.difficultyMax = dmax;
  if (value('dmin') !== '' && value('dmax') !== '' && dmin > dmax) {
    f.difficultyMin = dmax;
    f.difficultyMax = dmin;
  }
  const sensitive = root.querySelector<HTMLInputElement>(`[name="${n('sensitive')}"]`);
  if (sensitive?.checked === true) f.sensitive = true;
  return f;
}

/** "312 passende Fragen" for a game's types (and what else it needs, with `gameId`). */
export async function countPlayable(
  f: BankFilter,
  types: readonly string[],
  gameId?: string,
): Promise<number> {
  const q = filterQuery(
    { ...f, types: [...types], playable: true },
    gameId === undefined ? {} : { game: gameId },
  );
  const r = await api<{ count: number }>(`/api/bank/count${q}`);
  return r.count;
}
